import type { ReactNode } from 'react';
import { IconButton } from '@/components/ui/button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/dropdown';
import { IconCollapse, IconDots, IconExpand, IconPlus, IconX, ProviderIcon } from '@/components/ui/icons';
import { StatusDot } from '@/components/ui/status-dot';
import { cn } from '@/lib/utils';

export interface PaneMenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

/** Terminal pane card chrome: status dot, provider icon, mono name; ⋯ / expand / + / × on the right. */
export function Pane({
  title,
  status,
  provider,
  focused,
  expanded,
  onFocus,
  onExpand,
  onAdd,
  onClose,
  menu,
  children,
  className,
  paneId,
}: {
  title: ReactNode;
  status?: string | null;
  provider?: string | null;
  focused?: boolean;
  expanded?: boolean;
  onFocus?: () => void;
  onExpand?: () => void;
  onAdd?: () => void;
  onClose?: () => void;
  menu?: PaneMenuItem[];
  children: ReactNode;
  className?: string;
  /** Pane key, exposed as `data-pane` so the workspace can scroll a routed pane into view. */
  paneId?: string;
}) {
  return (
    <section
      data-pane={paneId}
      aria-label={typeof title === 'string' ? title : undefined}
      onPointerDown={onFocus}
      onFocusCapture={onFocus}
      className={cn(
        // `pane-scope` lets the optional "terminal stays dark" setting re-scope the dark tokens to panes.
        'pane-scope flex min-h-0 flex-col overflow-hidden rounded-lg border bg-pane text-pane-fg transition-colors',
        focused ? 'border-accent' : 'border-border',
        className,
      )}
    >
      <header className="flex h-8 touch:h-11 shrink-0 items-center gap-2 border-b border-border px-2.5">
        <StatusDot status={status ?? 'idle'} />
        {provider !== undefined ? (
          <ProviderIcon type={provider} size={13} className="shrink-0 text-fg-2" />
        ) : null}
        <h2 className="min-w-0 flex-1 truncate font-mono text-xs text-muted">{title}</h2>
        <div className="flex shrink-0 items-center gap-0.5">
          {menu?.length ? (
            <Menu>
              <MenuTrigger asChild>
                <IconButton label="Pane menu" className="h-6 w-6">
                  <IconDots size={14} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                {menu.map((m) => (
                  <MenuItem
                    key={m.label}
                    onSelect={m.onSelect}
                    className={m.danger ? 'text-danger' : undefined}
                  >
                    {m.label}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          ) : null}
          {onExpand ? (
            <IconButton
              label={expanded ? 'Restore pane' : 'Expand pane'}
              className="h-6 w-6 max-lg:hidden"
              onClick={onExpand}
            >
              {expanded ? <IconCollapse size={13} /> : <IconExpand size={13} />}
            </IconButton>
          ) : null}
          {onAdd ? (
            <IconButton label="New pane" className="h-6 w-6 max-lg:hidden" onClick={onAdd}>
              <IconPlus size={14} />
            </IconButton>
          ) : null}
          {onClose ? (
            <IconButton label="Close pane" className="h-6 w-6" onClick={onClose}>
              <IconX size={13} />
            </IconButton>
          ) : null}
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}
